import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavigatedComponent from "../NavigatedComponent";
import Header from "../../common/Header";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  DialogActions,
  Typography,
  DialogContent,
  DialogTitle,
  Container,
  Dialog,
  TextField,
  Paper,
  Pagination,
  PaginationItem,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import Sidenav from "../../common/Sidenav";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useCookies } from "react-cookie";
import { config } from "../../../config/config";
import { apiList, invokeApi } from "../../../services/apiServices";
import IconSidenav from "../../common/IconSidenav";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import SearchIcon from "@mui/icons-material/Search";

const PeopleList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies] = useCookies();
  const isUsersPage = location.pathname === "/people-manager/people-list";
  const [page, setPage] = useState(0);
  const isMobileScreen = useMediaQuery(
    "(min-width:360px) and (max-width:500px)"
  );
  const isSmallScreen = useMediaQuery(
    "(min-width:1024px) and (max-width:1440px)"
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    isMobileScreen ? 5 : isSmallScreen ? 5 : 10
  );
  const [rows, setRows] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);

  const columns = [
    { id: "item1", label: "Sl. No" },
    { id: "item2", label: "Profile Image" },
    { id: "item3", label: "Name" },
    { id: "item4", label: "Position" },
    { id: "item5", label: "Email" },
    { id: "item6", label: "Action" },
  ];
  
  const [showSideNav, setShowSideNav] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value.trim());
  };

  // Safe filtering - handle undefined rows
  const filteredRows = (rows || []).filter((row) => {
    const { name, position, email } = row || {};
    const lowerCaseQuery = searchQuery.toLowerCase();

    return (
      (name && name.toLowerCase().includes(lowerCaseQuery)) ||
      (position && position.toLowerCase().includes(lowerCaseQuery)) ||
      (email && email.toLowerCase().includes(lowerCaseQuery))
    );
  });

  const toggleSideNav = () => {
    setShowSideNav(!showSideNav);
  };
  
  const fetchTableData = async () => {
    try {
      setLoading(true);
      const response = await invokeApi(
        config.getMyCollege + apiList.getPeople,
        cookies
      );
      
      if (response?.status === 200) {
        setRows(response.data.people || []); 
      } else {
        console.error("Failed to fetch data:", response);
        setRows([]);
        showSnackbar("Failed to fetch people", "error");
      }
    } catch (error) {
      console.error("Error during data fetch:", error);
      setRows([]);
      showSnackbar("Error fetching people", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTableData();
  }, []);

  const handleView = async (id) => {
    const params = {
      id: id,
    };
    try {
      const response = await invokeApi(
        config.getMyCollege + apiList.getPerson,
        params,
        cookies
      );

      if (response?.status === 200) {
        const personData = response.data.person;
        navigate(`/people-manager/view-person/${id}`, {
          state: { personData },
        });
      } else {
        showSnackbar("Something went wrong. Please try again later!!", "error");
      }
    } catch (error) {
      console.error("Error during data fetch:", error);
      showSnackbar("Something went wrong. Please try again later!!", "error");
    }
  };

  const handleEdit = async (id) => {
    const params = {
      id: id,
    };
    try {
      const response = await invokeApi(
        config.getMyCollege + apiList.getPerson,
        params,
        cookies
      );

      if (response?.status === 200) {
        const personData = response.data.person;
        navigate(`/people-manager/edit-person/${id}`, {
          state: { personData },
        });
      } else {
        showSnackbar("Something went wrong. Please try again later!!", "error");
      }
    } catch (error) {
      console.error("Error during data fetch:", error);
      showSnackbar("Something went wrong. Please try again later!!", "error");
    }
  };

  const handlePersonDelete = async () => {
    try {
      const response = await invokeApi(
        config.getMyCollege + apiList.deletePerson,
        { id: deleteItemId },
        cookies
      );
      if (response?.status >= 200 && response?.status < 300) {
        if (response.data.responseCode === "200") {
          showSnackbar("Person deleted successfully", "success");
          setOpenDeleteDialog(false);
          setRows((prevRows) => prevRows.filter((row) => row.id !== deleteItemId));
        } else if (response.data.responseCode === "400") {
          showSnackbar("Failed to delete. Please try again later!!", "error");
        }
      }
    } catch (error) {
      showSnackbar("Failed to delete. Please try again later!!", "error");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  
  const handleOpenDeleteDialog = (id) => {
    setOpenDeleteDialog(true);
    setDeleteItemId(id);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  // Calculate pagination values safely
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage) || 1;
  const currentPage = Math.min(page, totalPages - 1);
  const paginatedRows = filteredRows.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Grid sx={{ display: "flex" }}>
        {isMobileScreen ? (
          <IconSidenav />
        ) : showSideNav ? (
          <IconSidenav />
        ) : (
          <Sidenav />
        )}
        <Grid component="main" sx={{ width: "100%", px: 1 }}>
          <Header toggleSideNav={toggleSideNav} />
          <Grid sx={{ py: 1 }}>
            <NavigatedComponent pathname={location.pathname} />
            <Grid
              sx={{
                width: "100%",
                px: isMobileScreen ? 1 : isSmallScreen ? 2 : 5,
              }}
            >
              <Grid
                container
                md={12}
                xs={12}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  my: 2,
                }}
              >
                <Grid item md={12} xs={12}>
                  <TextField
                    type="text"
                    size="small"
                    placeholder="Search by name, position, or email"
                    name="searchQuery"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#1D1A57" }} />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      style: {
                        height: isMobileScreen ? 10 : isSmallScreen ? 13 : 15,
                        fontSize: isMobileScreen ? 12 : isSmallScreen ? 12 : 14,
                      },
                    }}
                    sx={{
                      width: isMobileScreen
                        ? "250px"
                        : isSmallScreen
                        ? "300px"
                        : "400px",
                      background: "#f0f8ff",
                      borderRadius: "20px",
                      border: "1px solid #1D1A57",
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid
                sx={{
                  my: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isUsersPage && (
                  <>
                    {loading ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: isMobileScreen ? "100px" : "200px",
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        <Paper
                          sx={{
                            width: "100%",
                            boxShadow: 3,
                            py: 1,
                            maxWidth: isMobileScreen ? 275 : "none",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              px: 2,
                              py: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: isMobileScreen ? 1 : 10,
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <InputLabel
                                  sx={{
                                    mx: 1,
                                    fontSize: isMobileScreen ? 10 : 12,
                                  }}
                                >
                                  Row per page
                                </InputLabel>
                                <Select
                                  value={rowsPerPage}
                                  onChange={handleChangeRowsPerPage}
                                  sx={{
                                    height: 25,
                                    fontSize: isMobileScreen ? 10 : 12,
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      style: {
                                        maxHeight: "100px",
                                      },
                                    },
                                  }}
                                >
                                  <MenuItem
                                    value={isMobileScreen ? 5 : 10}
                                    sx={{
                                      fontSize: isMobileScreen ? 10 : 12,
                                      "&.MuiButtonBase-root": {
                                        minHeight: 0,
                                      },
                                    }}
                                  >
                                    {isMobileScreen ? 5 : 10}
                                  </MenuItem>
                                  <MenuItem
                                    value={isMobileScreen ? 10 : 20}
                                    sx={{
                                      fontSize: isMobileScreen ? 10 : 12,
                                      "&.MuiButtonBase-root": {
                                        minHeight: 0,
                                      },
                                    }}
                                  >
                                    {isMobileScreen ? 10 : 20}
                                  </MenuItem>
                                  <MenuItem
                                    value={isMobileScreen ? 15 : 30}
                                    sx={{
                                      fontSize: isMobileScreen ? 10 : 12,
                                      "&.MuiButtonBase-root": {
                                        minHeight: 0,
                                      },
                                    }}
                                  >
                                    {isMobileScreen ? 15 : 30}
                                  </MenuItem>
                                </Select>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <InputLabel
                                  sx={{
                                    mx: 1,
                                    fontSize: isMobileScreen ? 10 : 12,
                                  }}
                                >
                                  Go to page
                                </InputLabel>
                                <TextField
                                  type="number"
                                  variant="outlined"
                                  value={currentPage + 1}
                                  onChange={(event) => {
                                    const pageNumber = parseInt(
                                      event.target.value,
                                      10
                                    );
                                    if (pageNumber >= 1 && pageNumber <= totalPages) {
                                      handleChangePage(event, pageNumber - 1);
                                    }
                                  }}
                                  inputProps={{
                                    min: 1,
                                    max: totalPages,
                                    style: {
                                      height: "auto",
                                      fontSize: isMobileScreen ? 10 : 12,
                                      padding: "1px 7px",
                                    },
                                  }}
                                  sx={{ width: isMobileScreen ? 40 : 60 }}
                                />
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: isMobileScreen ? "none" : "flex",
                              }}
                            >
                              <Pagination
                                count={totalPages}
                                page={currentPage + 1}
                                onChange={(event, value) =>
                                  handleChangePage(event, value - 1)
                                }
                                color="primary"
                                renderItem={(item) => (
                                  <PaginationItem
                                    {...item}
                                    style={{
                                      fontSize: isMobileScreen ? 10 : 12,
                                    }}
                                  />
                                )}
                                shape="rounded"
                                boundaryCount={1}
                                siblingCount={1}
                                showFirstButton
                                showLastButton
                                firstIcon={<FirstPageRoundedIcon />}
                                lastIcon={<LastPageRoundedIcon />}
                                prevIcon={<ChevronLeftRoundedIcon />}
                                nextIcon={<ChevronRightRoundedIcon />}
                              />
                            </Box>
                          </Box>

                          <TableContainer
                            id="table-container"
                            sx={{
                              maxHeight: isMobileScreen ? 440 : 440,
                              overflowY: "auto",
                            }}
                          >
                            <Table aria-label="sticky table">
                              <TableHead
                                style={{
                                  background: "white",
                                  position: isMobileScreen
                                    ? "sticky"
                                    : "sticky",
                                  top: 0,
                                  zIndex: 5,
                                  textAlign: "left",
                                }}
                              >
                                <TableRow>
                                  {columns.map((column, index) => (
                                    <TableCell
                                      key={index}
                                      style={{
                                        width: `${100 / columns.length}%`,
                                        background: "#fff",
                                        zIndex: 100,
                                        backgroundColor: "#f0f8ff",
                                        fontWeight: 600,
                                        boxShadow: 2,
                                        whiteSpace: "nowrap",
                                        textAlign: "left",
                                      }}
                                    >
                                      <Grid
                                        sx={{
                                          display: "flex",
                                          gap: 0,
                                          fontSize: 13,
                                        }}
                                      >
                                        {column.label}
                                      </Grid>
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {paginatedRows.length === 0 ? (
                                  <TableRow>
                                    <TableCell
                                      colSpan={columns.length}
                                      align="center"
                                      style={{ py: "6px" }}
                                    >
                                      No people found
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  paginatedRows.map((row, index) => (
                                    <TableRow key={row.id || index}>
                                      <TableCell
                                        sx={{
                                          py: "4px",
                                          position: isMobileScreen
                                            ? "sticky"
                                            : "none",
                                          left: isMobileScreen ? 0 : "none",
                                          background: isMobileScreen
                                            ? "#f0f8ff"
                                            : "none",
                                          px: 2,
                                          fontSize: 12,
                                          textAlign: "left",
                                        }}
                                      >
                                        {index + 1 + currentPage * rowsPerPage}
                                      </TableCell>
                                      <TableCell
                                        sx={{ py: "6px", textAlign: "left" }}
                                      >
                                        <Grid sx={{ width: 25, height: 25 }}>
                                          <img
                                            src={row.imageUrl}
                                            alt="Profile"
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                              borderRadius: "50%",
                                            }}
                                          />
                                        </Grid>
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: "6px",
                                          textAlign: "left",
                                          "@media (min-width: 1440px) and (max-width:2000px)":
                                            {
                                              whiteSpace: "nowrap",
                                            },
                                          fontSize: 12,
                                        }}
                                      >
                                        {row.name}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: "6px",
                                          textAlign: "left",
                                          "@media (min-width: 1440px) and (max-width:2000px)":
                                            {
                                              whiteSpace: "nowrap",
                                            },
                                          fontSize: 12,
                                        }}
                                      >
                                        {row.position}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: "6px",
                                          textAlign: "left",
                                          "@media (min-width: 1440px) and (max-width:2000px)":
                                            {
                                              whiteSpace: "nowrap",
                                            },
                                          fontSize: 12,
                                        }}
                                      >
                                        {row.email}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: "6px",
                                          textAlign: "left",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        <Grid sx={{ display: "flex" }}>
                                          <Button
                                            sx={{
                                              marginRight: 2,
                                              height: "20px",
                                              my: "3px",
                                              fontSize: 11,
                                              "&.MuiButtonBase-root": {
                                                border: "1px solid #1565c0",
                                                minWidth: 30,
                                                py: 1,
                                              },
                                              "@media (min-width: 1440px) and (max-width:2000px)":
                                                {
                                                  my: 0,
                                                },
                                            }}
                                            onClick={() => handleView(row.id)}
                                          >
                                            <VisibilityOutlinedIcon
                                              style={{
                                                fontSize: 14,
                                                color: "#1565c0",
                                              }}
                                            />
                                          </Button>
                                          <Button
                                            sx={{
                                              marginRight: 2,
                                              height: "20px",
                                              my: "3px",
                                              fontSize: 11,
                                              "&.MuiButtonBase-root": {
                                                border: "1px solid #1b5e20",
                                                minWidth: 30,
                                                py: 1,
                                              },
                                              "@media (min-width: 1440px) and (max-width:2000px)":
                                                {
                                                  my: 0,
                                                },
                                            }}
                                            onClick={() => handleEdit(row.id)}
                                          >
                                            <EditIcon
                                              style={{
                                                fontSize: 14,
                                                color: "#1b5e20",
                                              }}
                                            />
                                          </Button>
                                          <Button
                                            sx={{
                                              marginRight: 2,
                                              height: "20px",
                                              my: "3px",
                                              fontSize: 11,
                                              "&.MuiButtonBase-root": {
                                                border: "1px solid #c62828",
                                                minWidth: 30,
                                                py: 1,
                                              },
                                              "@media (min-width: 1440px) and (max-width:2000px)":
                                                {
                                                  my: 0,
                                                },
                                            }}
                                            onClick={() =>
                                              handleOpenDeleteDialog(row.id)
                                            }
                                          >
                                            <DeleteIcon
                                              style={{
                                                fontSize: 14,
                                                color: "#c62828",
                                              }}
                                            />
                                          </Button>
                                        </Grid>
                                      </TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      </>
                    )}
                  </>
                )}

                <Dialog
                  open={openDeleteDialog}
                  onClose={handleCloseDeleteDialog}
                  fullWidth
                  maxWidth="xs"
                >
                  <Container>
                    <DialogTitle
                      sx={{
                        fontWeight: "600",
                        fontSize: "25px",
                        my: 1,
                        textAlign: "center",
                      }}
                    >
                      Confirm Delete
                    </DialogTitle>
                    <DialogContent>
                      <Grid container>
                        <Grid item md={12} sx={{ my: 1 }}>
                          <Typography>
                            Are you sure you want to delete this person?
                          </Typography>
                        </Grid>
                      </Grid>
                    </DialogContent>
                    <DialogActions>
                      <Grid
                        sx={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-around",
                        }}
                      >
                        <Button
                          onClick={handleCloseDeleteDialog}
                          sx={{
                            mb: 2,
                            fontSize: 12,
                            border: "1px solid #d11a2a",
                            color: "#d11a2a",
                            fontWeight: 600,
                            "&:hover": {
                              border: "1px solid #d11a2a",
                              color: "#d11a2a",
                            },
                            width: "40%",
                          }}
                        >
                          No
                        </Button>
                        <Button
                          onClick={handlePersonDelete}
                          color="primary"
                          sx={{
                            mb: 2,
                            fontSize: 12,
                            background: "#d11a2a",
                            color: "#fff",
                            fontWeight: 600,
                            "&:hover": {
                              background: "#d11a2a",
                              color: "#fff",
                            },
                            width: "40%",
                          }}
                        >
                          Yes
                        </Button>
                      </Grid>
                    </DialogActions>
                  </Container>
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                  open={openSnackbar}
                  autoHideDuration={4000}
                  onClose={handleCloseSnackbar}
                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                  sx={{ width: "auto" }}
                >
                  <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    sx={{ width: "auto" }}
                  >
                    {snackbarMessage}
                  </Alert>
                </Snackbar>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default PeopleList;